import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, FileText, Users, DollarSign } from 'lucide-react';
import { Card } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState({
    total_retenido: 0,
    retenciones_mes: 0,
    proveedores_activos: 0,
    retenciones_iva: 0,
    retenciones_islr: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/estadisticas`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) {
      return 'Bs. 0.00';
    }
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'VES',
      minimumFractionDigits: 2,
    }).format(numAmount);
  };

  const statCards = [
    {
      title: 'Total Retenido',
      value: formatCurrency(stats.total_retenido),
      icon: DollarSign,
      color: 'bg-blue-600',
      testId: 'stat-total-retenido',
    },
    {
      title: 'Retenciones Este Mes',
      value: stats.retenciones_mes,
      icon: FileText,
      color: 'bg-slate-900',
      testId: 'stat-retenciones-mes',
    },
    {
      title: 'Proveedores Activos',
      value: stats.proveedores_activos,
      icon: Users,
      color: 'bg-amber-500',
      testId: 'stat-proveedores',
    },
    {
      title: 'IVA Retenido',
      value: formatCurrency(stats.retenciones_iva),
      icon: TrendingUp,
      color: 'bg-blue-500',
      testId: 'stat-iva',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-slate-600">Cargando estadísticas...</div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900 font-heading">Dashboard</h1>
        <p className="text-sm text-slate-600 mt-2">Resumen de retenciones y proveedores</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card
              key={card.testId}
              data-testid={card.testId}
              className="bg-white border border-slate-200 shadow-sm rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0 pr-2">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider truncate">
                    {card.title}
                  </p>
                  <p className="text-xl font-bold text-slate-900 mt-1 font-mono truncate">{card.value}</p>
                </div>
                <div className={`${card.color} p-2 rounded-lg flex-shrink-0`}>
                  <Icon className="text-white" size={18} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-lg p-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-4 font-heading">Distribución de Retenciones</h2>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-slate-600">IVA</span>
                <span className="text-sm font-mono font-medium text-slate-900">
                  {formatCurrency(stats.retenciones_iva)}
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${(stats.retenciones_iva / (stats.retenciones_iva + stats.retenciones_islr || 1)) * 100}%`,
                  }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-slate-600">ISLR</span>
                <span className="text-sm font-mono font-medium text-slate-900">
                  {formatCurrency(stats.retenciones_islr)}
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div
                  className="bg-amber-500 h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${(stats.retenciones_islr / (stats.retenciones_iva + stats.retenciones_islr || 1)) * 100}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm rounded-lg p-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-4 font-heading">Acciones Rápidas</h2>
          <div className="space-y-3">
            <a
              href="/retenciones/crear"
              data-testid="quick-action-create"
              className="block w-full bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg rounded-md px-6 py-3 font-bold tracking-wide transition-all text-center"
            >
              Crear Nueva Retención
            </a>
            <a
              href="/proveedores"
              data-testid="quick-action-providers"
              className="block w-full bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 shadow-sm rounded-md px-6 py-3 font-medium transition-all text-center"
            >
              Gestionar Proveedores
            </a>
            <a
              href="/retenciones/historial"
              data-testid="quick-action-history"
              className="block w-full bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 shadow-sm rounded-md px-6 py-3 font-medium transition-all text-center"
            >
              Ver Historial
            </a>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;